import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ClipboardList, 
  Maximize2, 
  Loader2, 
  CheckCircle2, 
  Clock,
  Sun,
  Moon,
  Shield,
  Star,
  ChevronRight
} from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Button } from '../ui/button';

export default function MyStoreChecklistWidget({ expandable, expandPath }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchWidgetData();
  }, []);

  const fetchWidgetData = async () => {
    try {
      const result = await apiRequest('/store-checklists/my-store-widget');
      setData(result);
    } catch (error) {
      console.error('Failed to load my store checklist widget:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-4 h-full">
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  if (!data || data.error) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-4 h-full">
        <div className="text-center text-slate-500 py-8">
          {data?.error || 'Unable to load checklist data'}
        </div>
      </div>
    );
  }

  const { 
    store,
    checklists,
    total_pending,
    total_completed,
    kpi_points_today,
    kpi_points_this_week
  } = data;

  const getTypeIcon = (type) => {
    switch (type) {
      case 'opening': return <Sun className="h-4 w-4 text-orange-500" />;
      case 'closing': return <Moon className="h-4 w-4 text-indigo-500" />;
      case 'compliance': return <Shield className="h-4 w-4 text-green-500" />;
      default: return <ClipboardList className="h-4 w-4 text-slate-500" />;
    }
  };

  const getTypeBgColor = (type) => {
    switch (type) {
      case 'opening': return 'bg-orange-50 border-orange-200';
      case 'closing': return 'bg-indigo-50 border-indigo-200';
      case 'compliance': return 'bg-green-50 border-green-200';
      default: return 'bg-slate-50 border-slate-200';
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 h-full" data-testid="my-store-checklist-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-indigo-600" />
          <div>
            <h3 className="font-semibold text-slate-900 text-sm">My Checklists</h3>
            <p className="text-xs text-slate-500">{store}</p>
          </div>
        </div>
        {expandable && expandPath && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(expandPath)}
            className="h-8 w-8 p-0"
            data-testid="my-store-checklist-expand"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* KPI Summary */}
      <div className="flex items-center justify-between mb-3 p-2 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-100">
        <div className="flex items-center gap-2">
          <Star className="h-4 w-4 text-amber-500" />
          <span className="text-sm text-amber-700">KPI Points</span>
        </div>
        <div className="text-right">
          <span className="text-lg font-bold text-amber-600">{kpi_points_today}</span>
          <span className="text-xs text-amber-500 ml-1">today</span>
          <span className="text-xs text-slate-400 mx-1">|</span>
          <span className="text-sm font-medium text-amber-600">{kpi_points_this_week}</span>
          <span className="text-xs text-amber-500 ml-1">week</span>
        </div>
      </div>

      {/* Status Summary */}
      <div className="flex items-center justify-between text-sm mb-3">
        <div className="flex items-center gap-1.5">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          <span className="text-green-700 font-medium">{total_completed}</span>
          <span className="text-slate-400 text-xs">done</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Clock className="h-4 w-4 text-orange-500" />
          <span className="text-orange-700 font-medium">{total_pending}</span>
          <span className="text-slate-400 text-xs">pending</span>
        </div>
      </div>

      {/* Checklist Items */}
      <div className="space-y-2">
        {checklists.map((checklist) => (
          <div 
            key={checklist.template_id}
            className={`p-2.5 rounded-lg border ${getTypeBgColor(checklist.checklist_type)} ${
              checklist.status === 'pending' ? 'cursor-pointer hover:shadow-sm transition-shadow' : ''
            }`}
            onClick={() => checklist.status === 'pending' && navigate('/checklists')}
            data-testid={`checklist-item-${checklist.checklist_type}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {getTypeIcon(checklist.checklist_type)}
                <div>
                  <p className="text-sm font-medium text-slate-800 truncate max-w-[140px]">
                    {checklist.name.replace('Daily Store ', '').replace(' Checklist', '')}
                  </p>
                  <p className="text-xs text-slate-500">{checklist.item_count} items</p>
                </div>
              </div>
              
              {checklist.status === 'completed' ? (
                <div className="text-right">
                  <div className="flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span className="text-sm font-medium text-green-600">{checklist.completion_rate}%</span>
                  </div>
                  {checklist.kpi_points > 0 && (
                    <span className="text-xs text-amber-600">+{checklist.kpi_points} pts</span>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-1 text-orange-600">
                  <span className="text-xs font-medium">Complete</span>
                  <ChevronRight className="h-4 w-4" />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Quick Action */}
      {total_pending > 0 && (
        <Button 
          className="w-full mt-3 bg-indigo-600 hover:bg-indigo-700"
          size="sm"
          onClick={() => navigate('/checklists')}
          data-testid="complete-checklists-btn"
        >
          Complete {total_pending} Pending Checklist{total_pending > 1 ? 's' : ''}
        </Button>
      )}

      {total_pending === 0 && total_completed > 0 && (
        <div className="mt-3 p-2 bg-green-50 rounded-lg text-center">
          <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto mb-1" />
          <p className="text-sm text-green-700 font-medium">All done for today!</p>
        </div>
      )}
    </div>
  );
}
